/**
 * AdminsAndInvitesSection — Admin Portal Consolidation Step 4.
 *
 * Mounted INSIDE the Security tab below the US-only IP toggle.
 *
 * Two tables + one form, all admin-only (backend enforces via
 * `Depends(require_admin)`):
 *   1. Send invite form        →  POST /api/admin/invites
 *   2. Pending invites table   →  GET  /api/admin/invites?status=pending
 *                                 POST /api/admin/invites/{id}/revoke
 *   3. Current admins table    →  GET  /api/admin/admins
 *
 * No raw token is ever rendered or stored client-side. The accept URL
 * is shown ONCE on POST success so the inviting admin can copy it if
 * email delivery is in mock-mode (the email itself ships the link too).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Loader2, UserPlus, ShieldCheck, Copy, X } from "lucide-react";

function formatRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diffMs = d.getTime() - Date.now();
    const abs = Math.abs(diffMs);
    const mins = Math.round(abs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ${diffMs < 0 ? "ago" : "from now"}`;
    const hrs = Math.round(mins / 60);
    if (hrs < 48) return `${hrs}h ${diffMs < 0 ? "ago" : "from now"}`;
    const days = Math.round(hrs / 24);
    return `${days}d ${diffMs < 0 ? "ago" : "from now"}`;
  } catch {
    return iso;
  }
}

function isValidEmail(v) {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v || "").trim());
}

export default function AdminsAndInvitesSection() {
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteError, setInviteError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [pending, setPending] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  const [revoking, setRevoking] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [lastIssued, setLastIssued] = useState(null);

  const reload = useCallback(async () => {
    try {
      const [pendingRes, adminsRes] = await Promise.all([
        api.get("/admin/invites?status=pending"),
        api.get("/admin/admins"),
      ]);
      setPending(pendingRes.data || []);
      setAdmins(adminsRes.data || []);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || e.message
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const canSubmit = useMemo(
    () => isValidEmail(inviteEmail) && !submitting,
    [inviteEmail, submitting]
  );

  const submitInvite = async (e) => {
    e.preventDefault();
    if (!isValidEmail(inviteEmail)) {
      setInviteError("Enter a valid email.");
      return;
    }
    setInviteError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/invites", {
        email: inviteEmail.trim().toLowerCase(),
      });
      setLastIssued({ email: data.email, accept_url: data.accept_url });
      setInviteEmail("");
      toast.success(`Invite sent to ${data.email}.`);
      await reload();
    } catch (e) {
      const detail =
        formatApiErrorDetail(e.response?.data?.detail) || e.message;
      toast.error(detail);
      setInviteError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const doRevoke = async () => {
    if (!confirmRevoke) return;
    setRevoking(confirmRevoke.id);
    try {
      await api.post(`/admin/invites/${confirmRevoke.id}/revoke`);
      toast.success("Invite revoked.");
      setConfirmRevoke(null);
      await reload();
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || e.message
      );
    } finally {
      setRevoking(null);
    }
  };

  return (
    <Card
      className="mt-5 rounded-2xl border border-pp-border bg-pp-surface p-6"
      style={{ boxShadow: "var(--pp-card-shadow)" }}
      data-testid="admins-and-invites-card"
    >
      <header className="mb-4 flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-brand-orange" aria-hidden="true" />
        <h3 className="font-heading text-xl font-semibold text-pp-text">
          Admins &amp; Invites
        </h3>
      </header>
      <p className="mb-5 text-sm text-pp-text-muted">
        Invite teammates to share admin access. Links expire after 72 hours by
        default. Revoke any pending invite at any time.
      </p>

      {/* Send invite form */}
      <form
        onSubmit={submitInvite}
        className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]"
        data-testid="admin-invite-form"
      >
        <div>
          <Label htmlFor="admin-invite-email" className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
            Email address
          </Label>
          <Input
            id="admin-invite-email"
            type="email"
            placeholder="newadmin@example.com"
            value={inviteEmail}
            onChange={(e) => {
              setInviteEmail(e.target.value);
              if (inviteError) setInviteError(null);
            }}
            className={cn(
              "mt-2 h-11 rounded-full px-5",
              inviteError && "border-red-500"
            )}
            data-testid="admin-invite-email-input"
          />
          {inviteError && (
            <p className="mt-1 text-xs font-semibold text-red-600">{inviteError}</p>
          )}
        </div>
        <div className="flex items-end">
          <Button
            type="submit"
            disabled={!canSubmit}
            className="h-11 w-full rounded-full bg-pp-orange px-6 text-pp-cta-text hover:bg-pp-orange-hover sm:w-auto"
            data-testid="admin-invite-submit"
          >
            {submitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <UserPlus className="mr-2 h-4 w-4" />
            )}
            Send invite
          </Button>
        </div>
      </form>

      {/* Last issued accept URL — show once. */}
      {lastIssued && (
        <div
          className="mt-4 rounded-2xl border border-brand-orange/40 bg-brand-orange/5 px-4 py-3 text-sm"
          data-testid="admin-invite-last-issued"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold text-pp-text">
                Invite sent to {lastIssued.email}
              </p>
              <p className="mt-0.5 break-all text-xs text-pp-text-muted">
                {lastIssued.accept_url}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  navigator.clipboard
                    ?.writeText(lastIssued.accept_url)
                    .then(() => toast.success("Accept link copied."))
                    .catch(() => toast.error("Copy failed."));
                }}
                className="h-9 rounded-full"
                data-testid="admin-invite-copy-link"
              >
                <Copy className="mr-2 h-3.5 w-3.5" /> Copy link
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setLastIssued(null)}
                className="h-9 w-9 rounded-full p-0"
                data-testid="admin-invite-dismiss-last-issued"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Pending invites */}
      <div className="mt-6">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
          Pending invites
        </h4>
        {loading ? (
          <p className="mt-2 flex items-center gap-2 text-sm text-pp-text-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </p>
        ) : pending.length === 0 ? (
          <p className="mt-2 text-sm text-pp-text-muted">No pending invites.</p>
        ) : (
          <ul
            className="mt-3 divide-y divide-pp-border rounded-2xl border border-pp-border bg-pp-surface2"
            data-testid="admin-invite-pending-list"
          >
            {pending.map((inv) => (
              <li
                key={inv.id}
                className="flex flex-wrap items-center gap-3 px-4 py-3"
                data-testid={`admin-invite-pending-${inv.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-pp-text">{inv.email}</p>
                  <p className="text-xs text-pp-text-muted">
                    Invited by {inv.invited_by_name || inv.invited_by_email || "—"}
                    {" · "}
                    expires {formatRelative(inv.expires_at)}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setConfirmRevoke(inv)}
                  disabled={revoking === inv.id}
                  className="h-9 rounded-full text-red-700 hover:bg-red-100 dark:hover:bg-red-900/40"
                  data-testid={`admin-invite-revoke-${inv.id}`}
                >
                  {revoking === inv.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Revoke"
                  )}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Current admins */}
      <div className="mt-6">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
          Current admins ({admins.length})
        </h4>
        {loading ? (
          <p className="mt-2 flex items-center gap-2 text-sm text-pp-text-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </p>
        ) : admins.length === 0 ? (
          <p className="mt-2 text-sm text-pp-text-muted">No admins.</p>
        ) : (
          <ul
            className="mt-3 divide-y divide-pp-border rounded-2xl border border-pp-border bg-pp-surface2"
            data-testid="admin-invite-admins-list"
          >
            {admins.map((u) => (
              <li
                key={u.id}
                className="flex flex-wrap items-center gap-3 px-4 py-3"
                data-testid={`admin-invite-admin-${u.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-pp-text">
                    {u.name || u.email}
                  </p>
                  <p className="text-xs text-pp-text-muted">{u.email}</p>
                </div>
                <span className="rounded-full bg-pp-surface px-3 py-1 text-xs font-semibold text-pp-text-muted">
                  Joined {formatRelative(u.created_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Revoke confirmation dialog */}
      <Dialog
        open={!!confirmRevoke}
        onOpenChange={(v) => !v && setConfirmRevoke(null)}
      >
        <DialogContent className="rounded-3xl bg-pp-surface">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-pp-text">
              Revoke invite for {confirmRevoke?.email}?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-pp-text-muted">
            The current invite link will stop working immediately. You can send a
            fresh invite anytime.
          </p>
          <DialogFooter>
            <Button
              variant="ghost"
              type="button"
              onClick={() => setConfirmRevoke(null)}
              data-testid="admin-invite-revoke-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={doRevoke}
              className="rounded-full bg-red-600 text-white hover:bg-red-700"
              data-testid="admin-invite-revoke-confirm"
            >
              Revoke invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
